import React, { useState } from 'react';
import { Box, Paper, Typography, Grid, TextField, Button, FormControlLabel, Checkbox, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { bulkAPI } from '../services/api';
import { toast } from 'react-toastify';
import { formatTimeRange, isTimeRangeWrappingMidnight } from '../utils/timeUtils';
import { datePickerStyles, datePickerPopperStyles } from '../styles/datePickerStyles';

dayjs.locale('en-gb');

const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

const dayOptions = [
    { v: 1, label: 'Mon' },
    { v: 2, label: 'Tue' },
    { v: 3, label: 'Wed' },
    { v: 4, label: 'Thu' },
    { v: 5, label: 'Fri' },
    { v: 6, label: 'Sat' },
    { v: 7, label: 'Sun' },
];

const BulkBookingsPage = () => {
    const [form, setForm] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        courtNumber: 1,
        startDate: dayjs(),
        endDate: dayjs().add(30, 'day'),
        startHour: 6,
        startPeriod: 'PM',
        endHour: 7,
        endPeriod: 'PM',
        wrapsMidnight: false,
        status: 'PENDING',
    });
    const [days, setDays] = useState([1,2,3,4,5,6,7]);
    const [preview, setPreview] = useState([]);
    const [loading, setLoading] = useState(false);

    // Generate hour options (1-12)
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);

    const handleChange = (field) => (e) => {
        setForm({ ...form, [field]: e.target.value });
    };

    const handleDateChange = (field) => (newValue) => {
        setForm({ ...form, [field]: newValue });
    };

    const handleTimeChange = (field, value) => {
        const newForm = { ...form, [field]: value };
        setForm(newForm);
        
        // Auto-detect midnight wrapping
        const start24 = convertToTime24(newForm.startHour, newForm.startPeriod);
        const end24 = convertToTime24(newForm.endHour, newForm.endPeriod);
        const wraps = isTimeRangeWrappingMidnight(start24, end24);
        setForm({ ...newForm, wrapsMidnight: wraps });
    };

    const convertToTime24 = (hour, period) => {
        let h = parseInt(hour);
        if (period === 'AM' && h === 12) h = 0;
        if (period === 'PM' && h !== 12) h += 12;
        return `${String(h).padStart(2, '0')}:00`;
    };

    const toggleDay = (v) => (e) => {
        setDays(prev => e.target.checked ? [...new Set([...prev, v])] : prev.filter(x=>x!==v));
    };

    const toPayload = () => {
        const startTime24 = convertToTime24(form.startHour, form.startPeriod);
        const endTime24 = convertToTime24(form.endHour, form.endPeriod);
        
        const payload = {
            customerName: form.customerName,
            customerEmail: form.customerEmail,
            customerPhone: form.customerPhone,
            courtNumber: Number(form.courtNumber),
            startDate: form.startDate.format('YYYY-MM-DD'),
            endDate: form.endDate.format('YYYY-MM-DD'),
            startTime: startTime24,
            endTime: endTime24,
            daysOfWeek: days.sort((a,b)=>a-b),
            wrapsMidnight: form.wrapsMidnight,
            status: form.status,
        };
        
        console.log('📤 Bulk Booking Payload:', {
            formHours: `${form.startHour} ${form.startPeriod} - ${form.endHour} ${form.endPeriod}`,
            converted24: `${startTime24} - ${endTime24}`,
            payload
        });
        
        return payload;
    };

    const isValid = () => {
        if (!form.customerName?.trim()) { toast.error('Customer name is required'); return false; }
        const email = String(form.customerEmail || '').trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Valid email is required'); return false; }
        if (!String(form.customerPhone || '').trim()) { toast.error('Phone is required'); return false; }
        if (!days.length) { toast.error('Select at least one day'); return false; }
        return true;
    };

    const doPreview = async () => {
        if (!isValid()) return;
        setLoading(true);
        try {
            const res = await bulkAPI.preview(toPayload());
            setPreview(res.data || []);
        } catch (e) {
            toast.error('Preview failed');
        } finally {
            setLoading(false);
        }
    };

    const doApply = async () => {
        if (!isValid()) return;
        if (!preview.length) { toast.error('Run preview first'); return; }
        setLoading(true);
        try {
            const res = await bulkAPI.apply(toPayload());
            toast.success(`Created ${res.data?.length || 0} bookings`);
        } catch (e) {
            toast.error('Apply failed');
        } finally {
            setLoading(false);
        }
    };

    // Unified input field styles
    const inputFieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(196, 216, 53, 0.05)',
            borderRadius: '8px',
            '& fieldset': {
                borderColor: 'rgba(196, 216, 53, 0.3)',
                borderWidth: '1px'
            },
            '&:hover fieldset': {
                borderColor: logoColors.brightYellow,
            },
            '&.Mui-focused fieldset': {
                borderColor: logoColors.brightYellow,
                borderWidth: '2px'
            }
        },
        '& .MuiInputBase-input': {
            color: logoColors.brightYellow,
            fontWeight: 500,
            fontSize: '0.95rem'
        },
        '& .MuiInputLabel-root': {
            color: logoColors.brightYellow,
            fontWeight: 500,
            fontSize: '0.9rem',
            '&.Mui-focused': {
                color: logoColors.brightYellow,
            }
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: 2 }}>
                <Typography 
                    variant="h4" 
                    sx={{ 
                        color: logoColors.brightYellow, 
                        fontWeight: 700, 
                        mb: 3,
                        fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                >
                    Bulk / Recurring Bookings
                </Typography>
                
                <Paper 
                    elevation={6} 
                    sx={{ 
                        p: { xs: 3, md: 4 }, 
                        background: `linear-gradient(135deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)`,
                        borderRadius: '16px',
                        border: `1px solid ${logoColors.brightYellow}30`
                    }}
                >
                    {/* Row 1: Customer Name | Email | Phone | Court */}
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                        gap: 3,
                        mb: 3
                    }}>
                        <TextField 
                            fullWidth 
                            label="Customer Name" 
                            value={form.customerName} 
                            onChange={handleChange('customerName')}
                            sx={inputFieldStyles}
                        />
                        <TextField 
                            fullWidth 
                            label="Email" 
                            value={form.customerEmail} 
                            onChange={handleChange('customerEmail')}
                            sx={inputFieldStyles}
                        />
                        <TextField 
                            fullWidth 
                            label="Phone" 
                            value={form.customerPhone} 
                            onChange={handleChange('customerPhone')}
                            sx={inputFieldStyles}
                        />
                        <TextField 
                            fullWidth 
                            label="Court" 
                            type="number" 
                            value={form.courtNumber} 
                            onChange={handleChange('courtNumber')}
                            sx={inputFieldStyles}
                        />
                    </Box>
                    
                    {/* Row 2: Start Date | End Date | Start Time | End Time */}
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                        gap: 3,
                        mb: 3
                    }}>
                        <DatePicker
                            label="Start Date"
                            value={form.startDate}
                            onChange={handleDateChange('startDate')}
                            format="DD/MM/YYYY"
                            slotProps={{
                                ...datePickerPopperStyles,
                                textField: {
                                    fullWidth: true,
                                    InputProps: {
                                        sx: {
                                            color: '#c4d835',
                                            '& input': {
                                                color: '#c4d835 !important',
                                                WebkitTextFillColor: '#c4d835 !important'
                                            }
                                        }
                                    },
                                    sx: {
                                        ...datePickerStyles,
                                        ...inputFieldStyles
                                    }
                                }
                            }}
                        />
                        <DatePicker
                            label="End Date"
                            value={form.endDate}
                            onChange={handleDateChange('endDate')}
                            format="DD/MM/YYYY"
                            minDate={form.startDate}
                            slotProps={{
                                ...datePickerPopperStyles,
                                textField: {
                                    fullWidth: true,
                                    InputProps: {
                                        sx: {
                                            color: '#c4d835',
                                            '& input': {
                                                color: '#c4d835 !important',
                                                WebkitTextFillColor: '#c4d835 !important'
                                            }
                                        }
                                    },
                                    sx: {
                                        ...datePickerStyles,
                                        ...inputFieldStyles
                                    }
                                }
                            }}
                        />
                        
                        {/* Start Time Picker */}
                        <Box>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1,
                                p: 1.5,
                                borderRadius: '8px',
                                border: `1px solid rgba(196, 216, 53, 0.3)`,
                                backgroundColor: 'rgba(196, 216, 53, 0.05)',
                                height: '56px',
                                '&:hover': {
                                    borderColor: logoColors.brightYellow
                                }
                            }}>
                                <Typography sx={{ 
                                    color: logoColors.brightYellow, 
                                    fontWeight: 500, 
                                    fontSize: '0.9rem',
                                    minWidth: '80px'
                                }}>
                                    Start Time
                                </Typography>
                                <Select
                                    value={form.startHour}
                                    onChange={(e) => handleTimeChange('startHour', e.target.value)}
                                    variant="standard"
                                    sx={{ 
                                        color: logoColors.brightYellow, 
                                        minWidth: 60,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        '& .MuiSelect-icon': { color: logoColors.brightYellow },
                                        '&:before, &:after': { display: 'none' }
                                    }}
                                >
                                    {hours.map(h => <MenuItem key={h} value={h}>{h}:00</MenuItem>)}
                                </Select>
                                <Select
                                    value={form.startPeriod}
                                    onChange={(e) => handleTimeChange('startPeriod', e.target.value)}
                                    variant="standard"
                                    sx={{ 
                                        color: logoColors.brightYellow, 
                                        minWidth: 55,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        '& .MuiSelect-icon': { color: logoColors.brightYellow },
                                        '&:before, &:after': { display: 'none' }
                                    }}
                                >
                                    <MenuItem value="AM">AM</MenuItem>
                                    <MenuItem value="PM">PM</MenuItem>
                                </Select>
                            </Box>
                        </Box>

                        {/* End Time Picker */}
                        <Box>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1,
                                p: 1.5,
                                borderRadius: '8px',
                                border: `1px solid rgba(196, 216, 53, 0.3)`,
                                backgroundColor: 'rgba(196, 216, 53, 0.05)',
                                height: '56px',
                                '&:hover': {
                                    borderColor: logoColors.brightYellow
                                }
                            }}>
                                <Typography sx={{ 
                                    color: logoColors.brightYellow, 
                                    fontWeight: 500, 
                                    fontSize: '0.9rem',
                                    minWidth: '80px'
                                }}>
                                    End Time
                                </Typography>
                                <Select
                                    value={form.endHour}
                                    onChange={(e) => handleTimeChange('endHour', e.target.value)}
                                    variant="standard"
                                    sx={{ 
                                        color: logoColors.brightYellow, 
                                        minWidth: 60,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        '& .MuiSelect-icon': { color: logoColors.brightYellow },
                                        '&:before, &:after': { display: 'none' }
                                    }}
                                >
                                    {hours.map(h => <MenuItem key={h} value={h}>{h}:00</MenuItem>)}
                                </Select>
                                <Select
                                    value={form.endPeriod}
                                    onChange={(e) => handleTimeChange('endPeriod', e.target.value)}
                                    variant="standard"
                                    sx={{ 
                                        color: logoColors.brightYellow, 
                                        minWidth: 55,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        '& .MuiSelect-icon': { color: logoColors.brightYellow },
                                        '&:before, &:after': { display: 'none' }
                                    }}
                                >
                                    <MenuItem value="AM">AM</MenuItem>
                                    <MenuItem value="PM">PM</MenuItem>
                                </Select>
                                {form.wrapsMidnight && (
                                    <Typography sx={{ 
                                        color: logoColors.lightYellow, 
                                        fontSize: '0.75rem', 
                                        fontStyle: 'italic',
                                        ml: 'auto'
                                    }}>
                                        ⚠️ Next day
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Days of Week */}
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ color: logoColors.brightYellow, mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
                            Days of Week
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {dayOptions.map(d => (
                                <FormControlLabel 
                                    key={d.v}
                                    control={
                                        <Checkbox 
                                            checked={days.includes(d.v)} 
                                            onChange={toggleDay(d.v)} 
                                            sx={{ 
                                                color: logoColors.brightYellow,
                                                '&.Mui-checked': {
                                                    color: logoColors.brightYellow
                                                },
                                                py: 0.5
                                            }} 
                                        />
                                    }
                                    label={
                                        <Typography sx={{ 
                                            color: logoColors.brightYellow, 
                                            fontSize: '0.9rem',
                                            fontWeight: days.includes(d.v) ? 600 : 500
                                        }}>
                                            {d.label}
                                        </Typography>
                                    }
                                    sx={{ 
                                        m: 0,
                                        backgroundColor: days.includes(d.v) ? 'rgba(196, 216, 53, 0.1)' : 'transparent',
                                        borderRadius: '8px',
                                        px: 1,
                                        border: days.includes(d.v) ? `1px solid ${logoColors.brightYellow}` : '1px solid transparent'
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2, borderTop: `1px solid rgba(196, 216, 53, 0.2)` }}>
                        <Button 
                            variant="outlined" 
                            onClick={doPreview} 
                            disabled={loading}
                            sx={{ 
                                borderColor: logoColors.brightYellow, 
                                color: logoColors.brightYellow,
                                borderWidth: '2px',
                                px: 3,
                                py: 1,
                                borderRadius: '8px',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: logoColors.brightYellow,
                                    backgroundColor: 'rgba(196, 216, 53, 0.1)',
                                    borderWidth: '2px'
                                }
                            }}
                        >
                            Preview Bookings
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={doApply} 
                            disabled={loading}
                            sx={{ 
                                backgroundColor: logoColors.brightYellow, 
                                color: logoColors.darkBlue, 
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                px: 4,
                                py: 1,
                                borderRadius: '8px',
                                textTransform: 'none',
                                boxShadow: `0 4px 12px rgba(196, 216, 53, 0.3)`,
                                '&:hover': {
                                    backgroundColor: logoColors.lightYellow,
                                    boxShadow: `0 6px 16px rgba(196, 216, 53, 0.4)`
                                }
                            }}
                        >
                            Create Bookings
                        </Button>
                        </Box>
            </Paper>

            {preview.length > 0 && (
                <Paper elevation={2} sx={{ p: 2, mt: 2, background: `linear-gradient(135deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)` }}>
                        <Typography sx={{ color: logoColors.brightYellow, fontWeight: 700, mb: 1 }}>
                            Preview ({preview.length} bookings)
                        </Typography>
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {preview.map((p, i) => (
                                <Box key={i} sx={{ color: logoColors.brightYellow, mb: 0.5, fontSize: '0.9rem' }}>
                                    {dayjs(p.bookingDate).format('MMM DD, YYYY')} - Court {p.courtNumber} - {formatTimeRange(p.startTime, p.endTime)}
                                </Box>
                            ))}
                        </Box>
                </Paper>
            )}
        </Box>
        </LocalizationProvider>
    );
};

export default BulkBookingsPage;


